import { IPwaContent } from '@adminPortal/_common/_interfaces/IPwaContent';
import { IPwaPreviewConfig } from '@adminPortal/_common/_interfaces/IPwaPreviewConfig';
import { Inject, Injectable } from '@angular/core';
import { CookiesService } from '@pwaPortal/core/services/cookies/cookies.service';
import { AsyncStorage } from '@sharedLib/services/storage/async-storage.service';
import { NEW_PWA_CONFIG, PWA_CONFIG } from '@sharedLib/tokens/tokens';

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  private paramsKeys = ['pixel', 'sub1', 'sub2', 'sub3', 'sub4', 'sub5', 'sub6', 'sub7', 'sub8', 'sub9', 'sub10', 'fbclid'];

  private cookiesKeys = ['_fbp', '_fbc', '_ga', '_gtag'];

  constructor(
    @Inject(NEW_PWA_CONFIG) private config: IPwaPreviewConfig,
    private cookiesService: CookiesService,
    private storage: AsyncStorage
  ) {}

  public async initConfig() {
    // first we have to check if it's opened as PWA or landing page
    const isPwa = !!this.extractUrlInfo().pwaId;
    // if it's not pwa but landing page - save all config in db
    // because later on same domain, same route, might be config for another pwa
    if (!isPwa) {
      await this.storage.set(this.config.id, this.config);
    }
  }

  public async saveParams() {
    // first we have to check if it's opened as PWA or landing page
    const isPwa = !!this.extractUrlInfo().pwaId;
    if (!isPwa) {
      // getting params from url
      const searchParams = this.mappedParams(new URL(document.location.href).searchParams);
      const pwaId = this.getPwaId();

      // setting params to storage for later use in PWA
      for (const key of this.paramsKeys) {
        const value = searchParams.get(key);
        if (value) {
          await this.storage.set(`${key}_${pwaId}`, value);
        }
      }
      for (const key of this.cookiesKeys) {
        const value = this.cookiesService.get(key);
        if (value) {
          await this.storage.set(`${key}_${pwaId}`, value);
        }
      }
    }
  }

  public async getParams() {
    const pwaId = this.getPwaId();
    const params: any = {};
    for (const key of this.paramsKeys) {
      const value = await this.storage.get(`${key}_${pwaId}`);
      params[key] = value || '';
    }
    for (const key of this.cookiesKeys) {
      const value = await this.storage.get(`${key}_${pwaId}`);
      params[key] = value || '';
    }
    const visit_id = this.getUserId();
    params['visit_id'] = visit_id;

    return params;
  }

  private mappedParams(params: URLSearchParams): URLSearchParams {
    const newParams = new URLSearchParams();
    params.forEach((value, key) => {
      if (key && key !== 'null') {
        newParams.append(key.toLowerCase(), value);
      }
    });
    return newParams;
  }

  public extractUrlInfo(): { pwaId: string | null; isPwaMode: boolean } {
    const path = window.location.pathname;
    const pwaHtmlMatch = path.match(/\/pwa\/([^\/]+)\/pwa\.html/);
    if (pwaHtmlMatch) {
      return { pwaId: pwaHtmlMatch[1], isPwaMode: true };
    }

    const pwaMatch = path.match(/\/pwa\/([^\/]+)/);
    if (pwaMatch) {
      return { pwaId: pwaMatch[1], isPwaMode: false };
    }

    return { pwaId: null, isPwaMode: false };
  }

  public getIsPwaMode() {
    let isPwaMode = false;
    const urlInfo = this.extractUrlInfo();
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (
      document.referrer.startsWith('android-app://') ||
      'standalone' in navigator ||
      isStandalone ||
      urlInfo.isPwaMode ||
      isIOS
    ) {
      isPwaMode = true;
    }
    return isPwaMode;
  }

  public getPwaId(): string {
    const urlData = this.extractUrlInfo();
    return urlData.pwaId || this.config.id;
  }

  public getUserId(): string {
    return this.cookiesService.getUserId(this.getPwaId());
  }

  public async getIsPwaFirstOpen() {
    const pwaId = this.getPwaId();
    return !!(await this.storage.get(`firstVisit_${pwaId}`));
  }

  public async setPwaFirstOpen() {
    const pwaId = this.getPwaId();
    await this.storage.set(`firstVisit_${pwaId}`, true);
  }

  public async getPwaConfig() {
    // prioritise config from db, because on the same route might be config for another pwa
    const storageConfig: IPwaPreviewConfig = await this.storage.get(this.getPwaId());
    return this.formatPwaConfigFromBackend(storageConfig || this.config);
  }

  async getOfferUrl() {
    const pwaConfig = await this.getPwaConfig();
    let offerUrl = pwaConfig.offer?.url || '';
    const params = await this.getParams();
    Object.keys(params).forEach((key) => {
      offerUrl = offerUrl.replace(`{${key}}`, params[key]);
    });
    return offerUrl;
  }

  private formatPwaConfigFromBackend(pwa: any): IPwaPreviewConfig {
    return {
      ...pwa,
      content: this.formatPwaConfigContentFromBackend(pwa.content),
      offerUrl: pwa.offer?.url || '',
      // offer: pwa.offer?.id || null,
    };
  }

  private formatPwaConfigContentFromBackend(content: any): IPwaContent[] {
    return Object.keys(content)
      .map((key) => ({
        ...content[key],
        updateDate: {
          ...content[key].updateDate,
          dateTime: content[key].updateDate?.dateTime ? content[key].updateDate?.dateTime.split('T')[0] : null,
        },
        languageCode: key,
        comments: content[key].comments?.length
          ? content[key].comments.map((el: any) => ({
              ...el,
              date: {
                ...el.date,
                dateTime: el.date?.dateTime ? el.date.dateTime.split('T')[0] : null,
              },
              answerDate: {
                ...el.answerDate,
                dateTime: el.answerDate?.dateTime ? el.answerDate.dateTime.split('T')[0] : null,
              },
            }))
          : [],
      }))
      .sort((a, b) => (a.isDefault ? -1 : 1));
  }
}
